import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useConstants } from '@/contexts/ConstantsContext';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, FileText, CheckCircle2, XCircle, AlertTriangle, Download, Users, Shuffle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportLeads = () => {
  const navigate = useNavigate();
  const { users, sectors, getNichesForSector } = useConstants();
  const { user, isAdmin } = useAuth();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [defaultCountry, setDefaultCountry] = useState('UK');
  
  // Default sector/niche
  const [defaultSector, setDefaultSector] = useState('Other');
  const [defaultNiche, setDefaultNiche] = useState('Other');
  
  // Assignment options
  const [assignMode, setAssignMode] = useState('single'); // 'single', 'round_robin', 'unassigned'
  const [selectedOwner, setSelectedOwner] = useState('');
  const [roundRobinUsers, setRoundRobinUsers] = useState([]);
  
  // Duplicate handling mode - UPDATE is default per spec
  const [duplicateMode, setDuplicateMode] = useState('update'); // 'update' or 'skip'
  
  // Get available niches for selected default sector
  const availableNiches = defaultSector ? getNichesForSector(defaultSector) : [];

  // Redirect non-admins
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [isAdmin, navigate]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const toggleRoundRobinUser = (userId) => {
    setRoundRobinUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (assignMode === 'round_robin' && roundRobinUsers.length === 0) {
      toast.error('Please select at least one user for round robin');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      let url = `${API}/import/leads?default_country_market=${defaultCountry}&assign_mode=${assignMode}&default_sector=${encodeURIComponent(defaultSector)}&default_niche=${encodeURIComponent(defaultNiche)}&duplicate_action=${duplicateMode}`;
      
      if (assignMode === 'single') {
        // If selectedOwner is empty (meaning "Me"), use current user's ID
        const ownerId = selectedOwner || user?.id;
        if (ownerId) {
          url += `&default_owner_id=${ownerId}`;
        }
      } else if (assignMode === 'round_robin' && roundRobinUsers.length > 0) {
        url += `&round_robin_user_ids=${roundRobinUsers.join(',')}`;
      }

      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      toast.success('Import completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'full_name', 'company_name', 'phone', 'email', 'linkedin_url', 'website',
      'city_state', 'country_market', 'industry', 'lead_source', 'source_detail',
      'icp_fit', 'owner_id', 'preferred_phone', 'best_time_to_call', 'job_title',
      'company_size', 'priority', 'notes'
    ];
    
    const ownerIds = users.map(u => `${u.name}: ${u.id}`).join(' | ');
    const exampleRow = [
      'John Smith', 'Acme Inc', '+44 7700 900123', 'john@acme.com', '',
      'https://acme.com', 'London', 'UK', 'Technology', 'Google Maps manual',
      'Maps search: tech companies', 'Good', users[0]?.id || '', 'Main', 'Morning',
      'CEO', '11-50', 'High', 'Interested in AI solutions'
    ];

    const csv = [
      `# Owner IDs: ${ownerIds}`,
      `# Required: full_name, company_name, phone, country_market (UK/USA), industry, icp_fit (Good/Maybe/Bad), lead_source, owner_id`,
      headers.join(','),
      exampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lumero_leads_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="import-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Import Leads</h1>
        <p className="text-muted-foreground mt-1">Upload a CSV file to import leads in bulk</p>
      </div>

      {/* Template Download */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">CSV Template</CardTitle>
          <CardDescription>Download a template with the correct column headers</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={downloadTemplate} className="border-border" data-testid="download-template">
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">Upload CSV</CardTitle>
          <CardDescription>Select your CSV file to import</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">
              {file ? file.name : 'Click to select a CSV file'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {file ? `${(file.size / 1024).toFixed(1)} KB` : 'or drag and drop'}
            </p>
          </div>

          {file && (
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
              <FileText className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setFile(null); setResult(null); }}>
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Default Country Selection */}
          <div className="space-y-2 p-4 bg-secondary/30 rounded-lg border border-border">
            <Label className="text-sm font-medium">Default Country/Market</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Select the default market for leads that can't be auto-detected from the address
            </p>
            <div className="flex gap-2">
              <Button
                variant={defaultCountry === 'UK' ? 'default' : 'outline'}
                onClick={() => setDefaultCountry('UK')}
                className={defaultCountry === 'UK' ? '' : 'border-border'}
                data-testid="country-uk"
              >
                🇬🇧 UK
              </Button>
              <Button
                variant={defaultCountry === 'USA' ? 'default' : 'outline'}
                onClick={() => setDefaultCountry('USA')}
                className={defaultCountry === 'USA' ? '' : 'border-border'}
                data-testid="country-usa"
              >
                🇺🇸 USA
              </Button>
            </div>
          </div>

          {/* Default Sector/Niche Selection */}
          <div className="space-y-4 p-4 bg-secondary/30 rounded-lg border border-border">
            <Label className="text-sm font-medium">Default Sector & Niche</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Set default sector and niche for leads that don't have this info in the CSV
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Sector</Label>
                <Select 
                  value={defaultSector} 
                  onValueChange={(v) => { 
                    setDefaultSector(v); 
                    setDefaultNiche('Other'); 
                  }}
                >
                  <SelectTrigger className="bg-secondary border-border" data-testid="default-sector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Niche</Label>
                <Select 
                  value={defaultNiche} 
                  onValueChange={setDefaultNiche}
                  disabled={!defaultSector || defaultSector === 'Other'}
                >
                  <SelectTrigger className="bg-secondary border-border" data-testid="default-niche">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Other">Other</SelectItem>
                    {availableNiches.filter(n => n !== 'Other').map(n => (
                      <SelectItem key={n} value={n}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Duplicate Handling Mode */}
          <div className="space-y-4 p-4 bg-secondary/30 rounded-lg border border-border">
            <Label className="text-sm font-medium">Duplicate Handling</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Choose how to handle leads with matching phone numbers
            </p>
            
            <RadioGroup value={duplicateMode} onValueChange={setDuplicateMode} className="space-y-3">
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="update" id="dup-update" />
                <Label htmlFor="dup-update" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    <span className="font-medium">Update Existing</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Update existing leads with new data (won't overwrite non-empty fields with blank)
                  </p>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="skip" id="dup-skip" />
                <Label htmlFor="dup-skip" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    <span className="font-medium">Skip Duplicates</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ignore rows that match existing leads by phone number
                  </p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Assignment Mode Selection */}
          <div className="space-y-4 p-4 bg-secondary/30 rounded-lg border border-border">
            <Label className="text-sm font-medium">Lead Assignment</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Choose how to assign imported leads
            </p>
            
            <RadioGroup value={assignMode} onValueChange={setAssignMode} className="space-y-3">
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="single" id="single" />
                <Label htmlFor="single" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">Assign to Single User</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">All leads go to one selected user</p>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="round_robin" id="round_robin" />
                <Label htmlFor="round_robin" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Shuffle className="w-4 h-4" />
                    <span className="font-medium">Round Robin</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Distribute leads evenly across selected users</p>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors">
                <RadioGroupItem value="unassigned" id="unassigned" />
                <Label htmlFor="unassigned" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Leave Unassigned</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Import to unassigned queue for later assignment</p>
                </Label>
              </div>
            </RadioGroup>

            {/* Single User Selection */}
            {assignMode === 'single' && (
              <div className="mt-4">
                <Label className="text-sm">Select User</Label>
                <Select value={selectedOwner || "current"} onValueChange={(v) => setSelectedOwner(v === "current" ? "" : v)}>
                  <SelectTrigger className="bg-secondary border-border mt-2" data-testid="owner-select">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Me ({user?.name})</SelectItem>
                    {users.filter(u => u.id !== user?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Round Robin User Selection */}
            {assignMode === 'round_robin' && (
              <div className="mt-4 space-y-2">
                <Label className="text-sm">Select Users for Round Robin</Label>
                <div className="space-y-2 mt-2">
                  {users.map(u => (
                    <div 
                      key={u.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        roundRobinUsers.includes(u.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                      }`}
                      onClick={() => toggleRoundRobinUser(u.id)}
                    >
                      <Checkbox 
                        checked={roundRobinUsers.includes(u.id)} 
                        onCheckedChange={() => toggleRoundRobinUser(u.id)}
                      />
                      <div className="flex-1">
                        <span className="font-medium">{u.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">({u.role})</span>
                      </div>
                    </div>
                  ))}
                </div>
                {roundRobinUsers.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Leads will be distributed evenly to {roundRobinUsers.length} users
                  </p>
                )}
              </div>
            )}
          </div>

          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full"
            data-testid="upload-btn"
          >
            {uploading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import Leads
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card className="bg-card border-border" data-testid="import-results">
          <CardHeader>
            <CardTitle className="text-lg">Import Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-emerald-500/10 rounded-lg">
                <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                <p className="text-2xl font-bold text-emerald-400">{result.imported}</p>
                <p className="text-sm text-muted-foreground">Imported</p>
                <p className="text-xs text-muted-foreground mt-1">New leads created</p>
              </div>
              <div className="text-center p-4 bg-blue-500/10 rounded-lg">
                <RefreshCw className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                <p className="text-2xl font-bold text-blue-400">{result.updated || 0}</p>
                <p className="text-sm text-muted-foreground">Updated</p>
                <p className="text-xs text-muted-foreground mt-1">Existing leads updated</p>
              </div>
              <div className="text-center p-4 bg-amber-500/10 rounded-lg">
                <AlertTriangle className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                <p className="text-2xl font-bold text-amber-400">{result.skipped}</p>
                <p className="text-sm text-muted-foreground">Skipped</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {duplicateMode === 'skip' ? 'Duplicates or invalid' : 'Invalid rows'}
                </p>
              </div>
            </div>

            {result.updated > 0 && (
              <p className="text-xs text-muted-foreground bg-blue-500/10 p-3 rounded-lg">
                <strong>Updated</strong> means an existing lead matched by phone number was updated with new non-empty values from the CSV.
              </p>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="mt-4">
                <p className="font-medium text-destructive mb-2">Errors:</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {result.errors.map((error, idx) => (
                    <p key={idx} className="text-sm text-muted-foreground">{error}</p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">Import Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p><strong>Smart column detection:</strong> The system will automatically map common column names like "Name", "Phone", "Address", "Website", "Category" etc.</p>
          <p><strong>Minimum required:</strong> At least a Name or Phone column</p>
          <p><strong>Recognized columns:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Name/Contact:</strong> Name, Contact Name, Lead Name, Full Name</li>
            <li><strong>Company:</strong> Company, Business Name, Organization</li>
            <li><strong>Phone:</strong> Phone, Telephone, Mobile, Tel</li>
            <li><strong>Email:</strong> Email, Emails, E-mail</li>
            <li><strong>Address:</strong> Address, City, Location</li>
            <li><strong>Website:</strong> Website, URL, Web</li>
            <li><strong>Industry:</strong> Industry, Category, Sector</li>
          </ul>
          <p><strong>Default values applied:</strong> Country={defaultCountry}, Sector={defaultSector}, Niche={defaultNiche}, ICP=Maybe, Source=Other</p>
          <p className={`font-medium ${duplicateMode === 'update' ? 'text-blue-400' : 'text-amber-400'}`}>
            <strong>Duplicate handling:</strong> {duplicateMode === 'update' 
              ? 'Leads with matching phone numbers will be updated (not re-imported).' 
              : 'Leads with matching phone numbers will be skipped.'}
          </p>
          <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
            <p className="font-medium text-foreground mb-2">Import Results Key:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-emerald-400">Imported:</strong> New leads created (no matching phone found)</li>
              <li><strong className="text-blue-400">Updated:</strong> Existing leads updated with new non-empty values</li>
              <li><strong className="text-amber-400">Skipped:</strong> {duplicateMode === 'update' 
                ? 'Invalid rows (missing name and phone)' 
                : 'Duplicates ignored OR invalid rows (missing name and phone)'}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportLeads;
